/* eslint-disable no-inner-declarations */
/* eslint-disable @typescript-eslint/no-namespace */
import * as Sentry from '@sentry/remix';
import type { PAGE_POSITIONS } from 'types';
import type { ProductListItem } from 'types/capi/shared';
import {
  fromProductListItem,
  type TrackableProduct
} from 'types/TrackableProduct';
import type { User } from 'types/User';

const GTM_UNDEFINED_ERR_MSG =
  'window.dataLayer is not defined. This could mean your google analytics script has not loaded on the page yet.';

declare global {
  interface Window {
    dataLayer?: [unknown];
    gtag: (
      option: string,
      gaTrackingId: string,
      options: Record<string, unknown>
    ) => void;
  }
}

type DataLayerParams = {
  articleType?: string;
  pageType: string;
  user: User;
};

function pushToDataLayer(data: object) {
  if (!window.dataLayer) return Sentry.captureMessage(GTM_UNDEFINED_ERR_MSG);

  window.dataLayer.push(data);
}

namespace Ecommerce {
  type GtmItem = {
    index: number;
    item_brand?: string;
    item_category?: string; // gender
    item_category2?: string; // footwear | clothing
    item_id?: string; // style code
    item_name: string;
    item_variant?: string; // sub brand
    location_id?: string; //stock status
    price: number | null;
  };

  function flushEcommerceFromDataLayer() {
    pushToDataLayer({ ecommerce: null });
  }

  function pushEcommerceToDataLayer(ecommerceDataLayer: {
    ecommerce: object;
    event: string;
    stock_status?: string;
  }) {
    flushEcommerceFromDataLayer();

    return pushToDataLayer(ecommerceDataLayer);
  }

  export function getEcommerceItem(
    product: TrackableProduct,
    index: number = 0
  ): GtmItem {
    return {
      index: index + 1,
      item_brand: product.brand,
      item_category: product.genders?.join(', '),
      item_category2: product.sub_type,
      item_id: product.style_code,
      item_name: product.name,
      item_variant: product.model,
      location_id: product.stock_status,
      price: product.price.sale ?? product.price.regular
    };
  }

  export function viewListItem(product: TrackableProduct) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        currency: product.price.currency,
        items: [getEcommerceItem(product)],
        sole_market_option: getSoleMarketOption(product),
        value: product.price.sale ?? product.price.regular
      },
      event: 'view_item',
      stock_status: product.stock_status
    });
  }

  export function viewListItems(products: ProductListItem[], path: string) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        currency: products[0]?.price.currency,
        item_list_name: path,
        items: products.map((p) => getEcommerceItem(fromProductListItem(p)))
      },
      event: 'view_item_list'
    });
  }

  export function addToCart({
    buttonPosition,
    currency,
    affiliateName,
    isReseller,
    item,
    price,
    pagePosition
  }: {
    affiliateName: string;
    buttonPosition: number;
    currency?: string;
    isReseller: boolean;
    item: GtmItem;
    pagePosition: PAGE_POSITIONS;
    price: number | null;
  }) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        button_position: buttonPosition,
        currency,
        is_reseller: isReseller,
        items: [item],
        page_position: pagePosition,
        reseller: affiliateName,
        value: price
      },
      event: 'add_to_cart'
    });
  }

  export function addToWishlist(product: TrackableProduct) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        currency: product.price.currency,
        items: [getEcommerceItem(product)],
        sole_market_option: getSoleMarketOption(product),
        value: product.price.sale ?? product.price.regular
      },
      event: 'add_to_wishlist'
    });
  }
}

function getSoleMarketOption(product: TrackableProduct) {
  if (product.stock_listings)
    return product.stock_listings.affiliates.some(
      (a) => a.affiliate.id === 'affiliate:marketplace'
    );
}

function addDefaultDataToLayer({
  pageType,
  user,
  articleType
}: DataLayerParams) {
  return pushToDataLayer({
    account_id: user.id,
    article_type: articleType || '',
    is_buyer: user.profile?.is_buyer ?? false,
    is_logged_in: Boolean(user.profile),
    is_seller: user.profile?.is_seller ?? false,
    is_seller_verified: user.profile?.seller_registration_status === 'VERIFIED',
    page_type: pageType
  });
}

function sendUserInteractionEvent(action: string, pagePosition: string) {
  return pushToDataLayer({
    action,
    event: 'userInteraction',
    page_position: pagePosition
  });
}

type ProductFilterGtmEvent = {
  filterName: string;
  filterValue: string;
  pagePosition: string;
};

function sendPlpProductFilterEvent({
  filterName,
  filterValue,
  pagePosition
}: ProductFilterGtmEvent) {
  return pushToDataLayer({
    event: 'productFilter',
    filter_name: filterName,
    filter_value: filterValue,
    page_position: pagePosition
  });
}

export enum LoginSource {
  Apple = 'APPLE',
  Email = 'EMAIL',
  Google = 'GOOGLE',
  Meta = 'FACEBOOK'
}

function sendLoginEvent(uid: string | undefined = '', source: LoginSource) {
  return pushToDataLayer({
    account_id: uid,
    account_type: source,
    event: 'user_login'
  });
}

function loadPixels() {
  return pushToDataLayer({ event: 'load_pixels' });
}

export type TrackedButtonClickName =
  | 'ALL_SIZES'
  | 'UK_SITE'
  | 'BLACKFRIDAY_BANNER'
  | 'GATED_RETAILER_CLICK'
  | 'LATEST_FEATURES'
  | 'BRAND_CARD'
  | 'COMPARE_ALL_RETAILERS'
  | 'DISCOVER_DEALS'
  | 'DISCOVER_MORE_FEATURES'
  | 'DOWNLOAD_THE_APP'
  | 'EDITORIAL_POLICY'
  | 'EXISTING_USER_LOGIN_PROMPT'
  | 'GO_TO_REGION'
  | 'LATEST_NEWS'
  | 'MOBILE_FILTERS_CLEAR_ALL'
  | 'MID_PROMO_BANNER'
  | 'MOBILE_FILTERS_SHOW_RESULTS'
  | 'NOTIFICATION_CENTRE'
  | 'NOTIFY_ME_SIZE_MARKET_CTA'
  | 'OPEN_NOTIFICATION'
  | 'PRODUCT_CARD'
  | 'PRODUCT_CLICK'
  | 'REGISTER_AS_A_SELLER'
  | 'RELATED_ARTICLES'
  | 'SALE_PRODUCTS_HIGHLIGHT_CTA'
  | 'SAVE_PRODUCT'
  | 'SELL_NOW'
  | 'SHARE_TO_FACEBOOK'
  | 'SHARE_TO_TWITTER'
  | 'SHOW_ALL_RAFFLES'
  | 'SHOW_ALL_RETAILERS'
  | 'SIGN_IN_APPLE'
  | 'SIGN_IN_EMAIL'
  | 'SIGN_IN_FACEBOOK'
  | 'SIGN_IN_GOOGLE'
  | 'SIGN_UP_EMAIL_PROMPT'
  | 'SIGN_UP_EMAIL_SUBSCRIPTION_BANNER'
  | 'SIGN_UP_EMAIL'
  | 'SILHOUETTE_CARD'
  | 'SOCIAL_MEDIA_CAROUSEL'
  | 'SOCIAL_MEDIA_PROFILE'
  | 'SPLASH_CTA'
  | 'STAY_ON_UK'
  | 'TOP_CATEGORY'
  | 'TOP_PROMO_BANNER'
  | 'USP_CARD'
  | 'VIEW_ALL'
  | 'VIEW_MORE'
  | 'WHATS_HOT'
  | 'WTB_VIEW_TOGGLE_RAFFLES'
  | 'WTB_VIEW_TOGGLE_RETAILERS'
  | `GRID_SIZE_TOGGLE_${string}`
  | `PAGINATION_${string}`
  | `SIZE_${string}`
  | `Sole Market Hub - ${string}`
  | `HOMEPAGE_CAROUSEL_${string}`
  | `PLP_CAROUSEL_${string}`
  | `SOLE_MARKET_FAQ_${string}`
  | `SPLASH_POSITION_${string}`
  | `FEATURES_${string}`
  | `PRODUCT_CARD_${string}`;

type ButtonClickEventData = {
  buttonName: TrackedButtonClickName;
  pagePosition: string;
};

function trackedButtonClick({
  buttonName,
  pagePosition
}: ButtonClickEventData) {
  return pushToDataLayer({
    button_name: buttonName,
    event: 'TrackedButtonClick',
    page_position: pagePosition
  });
}

function recordFeatureFlag(name: string, isEnabled: boolean) {
  return pushToDataLayer({
    event: 'feature_flag',
    feature_flag_is_enabled: isEnabled,
    feature_flag_name: name
  });
}

export default {
  Ecommerce,
  addDefaultDataToLayer,
  loadPixels,
  pushToDataLayer,
  recordFeatureFlag,
  sendLoginEvent,
  sendPlpProductFilterEvent,
  sendUserInteractionEvent,
  trackedButtonClick
};
